import { Action, Alert, ButtonGroup, Card, Icon } from 'corso-admin-ui';
import {
  automationsDisplayName,
  eventTypeDescription,
  Pegasus,
} from 'corso-types';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import GridList from '~/components/GridList';
import { AutomationListByEventType } from '~/features/settings/automations/AutomationListByEventType';
import { eventTypeIcon } from '~/features/settings/automations/automationsContent';
import { useAutomationsByEventType } from '~/features/settings/hooks/useAutomations';
import { useStoreId } from '~/providers/StoreUserProvider';

const shippingClaimEventTypes = [
  Pegasus.EventType.shipClaimAllowedResolutions,
  Pegasus.EventType.shipClaimReorderLocations,
  Pegasus.EventType.shipClaimSlaAlert,
  Pegasus.EventType.shipClaimReorderShipping,
  Pegasus.EventType.shipApplyOrderTags,
] as const satisfies Pegasus.EventType[];

function useShippingClaimRules() {
  const automationsByEventType = useAutomationsByEventType();
  return new Map(
    shippingClaimEventTypes.map((eventType) => [
      eventType,
      automationsByEventType.get(eventType) ?? [],
    ]),
  );
}

const ShippingClaimAddRule = () => {
  const storeId = useStoreId();
  const location = useLocation();

  const categories = shippingClaimEventTypes.map((eventType) => ({
    eventType,
    label: automationsDisplayName.eventType[eventType],
    description: eventTypeDescription[eventType],
    icon: eventTypeIcon[eventType] ?? 'aiInline',
  }));

  return (
    <GridList columns={2} data={categories}>
      {({ eventType, label, description, icon }) => (
        <GridList.ItemLink
          key={eventType}
          to={{
            pathname: `/${storeId}/settings/automations/create`,
            search: new URLSearchParams({
              eventType,
              returnTo: location.pathname,
            }).toString(),
          }}
          label={label}
          description={description}
          icon={<Icon icon={icon} />}
          variant="primary"
        />
      )}
    </GridList>
  );
};

export const ShippingClaimRules = () => {
  const shippingClaimRules = useShippingClaimRules();
  const hasShippingClaimRules = [...shippingClaimRules.values()].some(
    (rules) => rules.length,
  );

  // Reactively default to 'list' once rules are loaded, but freeze the choice
  // as soon as the user toggles either button.
  const [userView, setView] = useState<'list' | 'create' | null>(null);
  const view = userView ?? (hasShippingClaimRules ? 'list' : 'create');

  return (
    <Card>
      <Alert
        variant="info"
        heading="Shipping Claim automations are only visible to Corso Admins."
      />
      <Card.Line as="header">
        <Card.Heading>Automations</Card.Heading>

        <ButtonGroup horizontal>
          <Action
            onClick={() => setView('list')}
            pressed={view === 'list'}
            icon="rule"
            accessibilityLabel="View existing automations"
            disabled={!hasShippingClaimRules}
          >
            View
          </Action>
          <Action
            onClick={() => setView('create')}
            pressed={view === 'create'}
            icon="add"
            accessibilityLabel="Create a new automation"
          >
            Create
          </Action>
        </ButtonGroup>
      </Card.Line>

      {view === 'create' || !hasShippingClaimRules ? (
        <ShippingClaimAddRule />
      ) : (
        [...shippingClaimRules.entries()].map(([eventType, automations]) => {
          if (!automations.length) return null;

          return (
            <AutomationListByEventType
              key={eventType}
              eventType={eventType}
              draggable
            />
          );
        })
      )}
    </Card>
  );
};
